import React, { useState, useEffect } from 'react'
import { Form, useFormikContext } from 'formik'
import { useTranslate } from 'react-admin'
import { Divider } from '@material-ui/core'
import PropTypes from 'prop-types'
import ScheduleIcon from '@material-ui/icons/Schedule'
import VideoLibraryOutlinedIcon from '@material-ui/icons/VideoLibraryOutlined'

import '../../../../../utils/constants/CSS/eventForms.css'
import { isFormFieldDisabled } from '../../../../../utils/helpers/isFieldDisabled'
import FormContainer from '../../../../layout/FormContainer/FormContainer'
import MyDateField from '../../../fields/MyDateField'
import MyTimeField from '../../../fields/MyTimeField'
import MySelectField from '../../../fields/MySelectField/MySelectField'
import MyMultilineTextField from '../../../fields/MyMultilineTextField'
import IsUserAddonsAuthorized from '../../../../IsUserAddonsAuthorized/IsUserAddonsAuthorized'
import EventFormLivestreamFields from '../../../../EventFormLivestreamFields/EventFormLivestreamFields'
import GetFormDirtyValue from '../../../../getFormDirtyValue/getFormDirtyValue'
import AutoChangeFakeEvent from '../../../AutoChangeFakeEvent'
import onEventFieldChange from '../../../../../utils/helpers/eventHelpers/onEventFieldChange'
import { USER_ADDONS } from '../../../../../utils/constants/userAddons'
import isUserAddonsValid from '../../../../../utils/helpers/isUserAddonsValid'
import EventFormCourtLogos from '../../../../EventFormCourtLogos/EventFormCourtLogos'
import EventFormSharedRevenueToggle from '../../../../EventFormSharedRevenueToggle/EventFormSharedRevenueToggle'
import GAME_EVENT_FORM_BROADCAST_FIELDS from '../../../../../utils/constants/gameEventFormBroadcastFields'
import courtType from '../../../../../types/courtType'
import mutationType from '../../../../../types/mutationType'
import teamType from '../../../../../types/teamType'
import eventVariantType from '../../../../../types/events/eventVariantType'
import eventType from '../../../../../types/events/eventType'
import eventRawType from '../../../../../types/events/eventRawType'
import isCourtLivestreamEnabled from '../../../../../utils/helpers/isCourtLivestreamEnabled'
import AddCourtModal from '../../../../ui/AddCourtModal/AddCourtModal'
import AddButton from '../../../../ui/AddButton/AddButton'
import GameSubmitConfirmationModal from '../../../../GameSubmitConfirmationModal/GameSubmitConfirmationModal'
import ids from '../../../../../utils/constants/introIds'
import EventTeamsFields from '../EventTeamsFields/EventTeamsFields'
import MyCourtIcon from '../../../../../assets/icons/MyCourtIcon'
import ORGANISATION_TYPES from '../../../../../utils/constants/OrganisationTypes'
import ConnectedGameField from './ConnectedGameField'
import useGamePairingCandidates, { getClubTeamId } from './useGamePairingCandidates'
import ORGANISATION_SPORTS from '../../../../../utils/constants/organisationSports'
import { STREAM_FEATURES, supports } from '../../../../../utils/constants/streamTypeCapabilities'
import GameDrawerTabs from '../../../../GameFormLayout/GameDrawerTabs'
import SectionFieldset from '../../../../GameFormLayout/SectionFieldset'
import ThirdPartyStreamField from '../../../../GameFormLayout/ThirdPartyStreamField'
import SubmitErrorNavigator from '../../../../GameFormLayout/SubmitErrorNavigator'

// Fields the validators can flag, in top-to-bottom visual order, mapped to their tab.
// Volleyball has no Stats tab, so no statisticianId entry.
const FIELD_ORDER = [
  { field: 'fromDate', tab: 'details' },
  { field: 'startTime', tab: 'details' },
  { field: 'endTime', tab: 'details' },
  { field: 'courtId', tab: 'details' },
  { field: 'homeTeam', tab: 'details' },
  { field: 'awayTeam', tab: 'details' },
  { field: 'streamDestination', tab: 'streaming' },
]

const VolleyGameEventFormFields = ({
  event,
  state,
  orgType,
  courts,
  teams,
  selectedEventType,
  clickedEventId,
  eventsRaw,
  setEvents,
  setFormDirty,
  allowOrgYoutube,
  broadcastId,
  setMediaIsUploading,
  isUploadingModalOpen,
  setIsUploadingModalOpen,
  onGameSubmit,
}) => {
  const { values, setFieldValue, errors, submitCount } = useFormikContext()
  const translate = useTranslate()
  const [isCourtModalOpen, setIsCourtModalOpen] = useState(false)
  const [activeTab, setActiveTab] = useState('details')
  const hasTabError = (tab) =>
    submitCount > 0 && FIELD_ORDER.some((f) => f.tab === tab && errors[f.field])
  // Streaming tab only when the org holds the Livestream addon, mirroring the addon gate on the
  // tab's content so we never surface a tab that would render empty.
  const showStreamingTab = isUserAddonsValid([USER_ADDONS.LIVESTREAM])
  // Streaming is only meaningful on a livestream-capable court, so the tab stays disabled until
  // such a court is picked (in Details); a tooltip explains why.
  const streamingTabDisabled = !isCourtLivestreamEnabled(courts, values?.courtId)
  const selectedCourt = courts?.find((item) => item.id === values.courtId)
  const tabs = [
    { key: 'details', label: translate('ra.gameDrawer.tabs.details'), error: hasTabError('details') },
    ...(showStreamingTab
      ? [
          {
            key: 'streaming',
            label: translate('ra.gameDrawer.tabs.streaming'),
            showDot: Boolean(
              values[GAME_EVENT_FORM_BROADCAST_FIELDS.IS_BROADCAST] ||
                values[GAME_EVENT_FORM_BROADCAST_FIELDS.IS_RECORD],
            ),
            error: hasTabError('streaming'),
            disabled: streamingTabDisabled,
            disabledHint: translate('ra.gameDrawer.noStreamCourt'),
          },
        ]
      : []),
  ]

  // If the court is changed to one without streaming, don't leave the user stranded on the
  // now-disabled streaming tab.
  useEffect(() => {
    if (activeTab === 'streaming' && streamingTabDisabled) setActiveTab('details')
  }, [activeTab, streamingTabDisabled])
  const { onFromDateFieldChange, onStartTimeFieldChange, onEndTimeFieldChange } = onEventFieldChange

  const isClub = orgType === ORGANISATION_TYPES.ORG_CLUB
  const gamePairingOptions = useGamePairingCandidates({
    clubTeamId: isClub ? getClubTeamId(values.teams) : null,
    existingPairedGame: event?.pairedGame,
    enabled: isClub,
  })

  // Court-logo overlays apply to the produced tiers, so the ADS section shows only for those.
  const isLivestreamTypeFull =
    (values[GAME_EVENT_FORM_BROADCAST_FIELDS.IS_BROADCAST] ||
      values[GAME_EVENT_FORM_BROADCAST_FIELDS.IS_RECORD]) &&
    supports(values[GAME_EVENT_FORM_BROADCAST_FIELDS.TYPE], STREAM_FEATURES.COURT_LOGOS)

  // noValidate: hidden (display:none) required fields in inactive tabs otherwise abort native
  // submit ("invalid form control is not focusable"); let Formik validation +
  // SubmitErrorNavigator handle it instead.
  return (
    <Form id={ids.createEvent.form} noValidate>
      <GameDrawerTabs active={activeTab} onChange={setActiveTab} tabs={tabs} />

      <div style={{ display: activeTab === 'details' ? 'block' : 'none' }}>
        <FormContainer>
          <div className='div-flex'>
            <ScheduleIcon className='icon' style={{ fill: 'grey' }} />
            <div id={ids.createEvent.time} style={{ display: 'flex', gap: 12, flex: 1 }}>
              <div style={{ flex: 1 }}>
                <MyDateField
                  id={ids.createEvent.date}
                  name='fromDate'
                  label={translate('ra.formFields.date')}
                  required
                  disabled={isFormFieldDisabled(state)}
                  onChange={onFromDateFieldChange}
                />
              </div>
              <div style={{ flex: 1 }}>
                <MyTimeField
                  name='startTime'
                  label={translate('ra.formFields.startTime')}
                  required
                  disabled={isFormFieldDisabled(state)}
                  onChange={onStartTimeFieldChange}
                />
              </div>
              <div style={{ flex: 1 }}>
                <MyTimeField
                  name='endTime'
                  label={translate('ra.formFields.endTime')}
                  required
                  disabled={isFormFieldDisabled(state, 'endTime')}
                  onChange={onEndTimeFieldChange}
                />
              </div>
            </div>
          </div>

          <div id={ids.createEvent.court} className='div-flex'>
            <MyCourtIcon />
            <MySelectField
              name='courtId'
              label={translate('ra.formFields.courtName')}
              options={courts}
              emptyText={translate('ra.text.emptyCourts')}
              fullWidth
              required={Boolean(
                values[GAME_EVENT_FORM_BROADCAST_FIELDS.IS_BROADCAST] ||
                  values[GAME_EVENT_FORM_BROADCAST_FIELDS.IS_RECORD],
              )}
              disabled={isFormFieldDisabled(state)}
              addOptionsComponent={
                <AddButton
                  onClick={() => setIsCourtModalOpen(true)}
                  label={translate('ra.action.add_court')}
                  noHoverEffect={courts?.length}
                  style={{
                    paddingLeft: !courts?.length ? 8 : 4,
                    marginRight: !courts?.length ? 10 : 0,
                  }}
                />
              }
            />
            <AddCourtModal
              isCourtModalOpen={isCourtModalOpen}
              setIsCourtModalOpen={setIsCourtModalOpen}
              setValue={(newCourtValue) => setFieldValue('courtId', newCourtValue)}
            />
          </div>

          <div id={ids.createEvent.team}>
            <Divider style={{ margin: '4px 0 16px' }} />
            <span
              style={{
                display: 'block',
                marginBottom: 12,
                textTransform: 'uppercase',
                fontSize: 11,
                fontWeight: 700,
                letterSpacing: '.08em',
                color: '#9A9A9A',
              }}
            >
              {translate('ra.gameDrawer.sections.matchup')}
            </span>
            <EventTeamsFields
              state={state}
              orgType={orgType}
              teams={teams}
              preFilledAway={true}
            />
          </div>

          <MyMultilineTextField
            name='notes'
            label={translate('ra.formFields.notes')}
            multiline
            minRows={5}
            fullWidth
            disabled={isFormFieldDisabled(state)}
          />

          <ConnectedGameField
            renderValue={gamePairingOptions.length > 0}
            options={gamePairingOptions}
            state={state}
          />

          <ThirdPartyStreamField state={state} />
        </FormContainer>
      </div>

      <div style={{ display: activeTab === 'streaming' ? 'block' : 'none' }}>
        <FormContainer>
          <IsUserAddonsAuthorized acceptedAddons={[USER_ADDONS.LIVESTREAM]}>
            {isCourtLivestreamEnabled(courts, values?.courtId) ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
                <EventFormLivestreamFields
                  state={state}
                  gameStateTrackingAutoEnabled={false}
                  allowOrgYoutube={allowOrgYoutube}
                  broadcast={event?.broadcast}
                  broadcastId={broadcastId ?? null}
                  gameId={clickedEventId ?? null}
                  selectedCourt={selectedCourt}
                  selectedEventType={selectedEventType}
                  showGameStateTracking={false}
                />
                {isLivestreamTypeFull && (
                  <SectionFieldset
                    label={translate('ra.gameDrawer.sections.ads')}
                    style={{ paddingBottom: 18 }}
                  >
                    <EventFormSharedRevenueToggle state={state} />
                    <EventFormCourtLogos
                      state={state}
                      setMediaIsUploading={setMediaIsUploading}
                      selectedEventType={selectedEventType}
                      sport={ORGANISATION_SPORTS.VOLLEYBALL}
                    />
                  </SectionFieldset>
                )}
              </div>
            ) : (
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 12,
                  backgroundColor: '#F7F6F4',
                  borderRadius: 10,
                  padding: '14px 16px',
                }}
              >
                <VideoLibraryOutlinedIcon style={{ color: '#A0A0A0' }} />
                <span style={{ fontSize: 13, color: '#6B6B6B' }}>
                  {translate('ra.gameDrawer.noStreamCourt')}
                </span>
              </div>
            )}
          </IsUserAddonsAuthorized>
        </FormContainer>
      </div>

      <SubmitErrorNavigator
        fieldOrder={FIELD_ORDER}
        activeTab={activeTab}
        setActiveTab={setActiveTab}
      />
      <GetFormDirtyValue setFormDirty={setFormDirty} />
      <AutoChangeFakeEvent
        clickedEventId={clickedEventId}
        selectedEventType={selectedEventType}
        eventsRaw={eventsRaw}
        setEvents={setEvents}
      />
      <GameSubmitConfirmationModal
        isOpen={isUploadingModalOpen}
        onCancelClick={() => setIsUploadingModalOpen(false)}
        onCreateClick={() => {
          setIsUploadingModalOpen(false)
          onGameSubmit(values)
        }}
      />
    </Form>
  )
}

VolleyGameEventFormFields.propTypes = {
  event: eventType,
  state: PropTypes.string,
  orgType: PropTypes.string,
  courts: PropTypes.arrayOf(courtType),
  teams: PropTypes.arrayOf(teamType),
  selectedEventType: eventVariantType,
  clickedEventId: PropTypes.string,
  eventsRaw: PropTypes.arrayOf(eventRawType),
  setEvents: PropTypes.func,
  setFormDirty: PropTypes.func,
  allowOrgYoutube: PropTypes.bool,
  broadcastId: PropTypes.string,
  mediaIsUploading: PropTypes.bool,
  setMediaIsUploading: PropTypes.func,
  isUploadingModalOpen: PropTypes.bool,
  setIsUploadingModalOpen: PropTypes.func,
  onGameSubmit: PropTypes.func,
  createEvent: mutationType,
  updateEvent: mutationType,
}

export default VolleyGameEventFormFields
